/**
 * gradeArtifact Stage 0 — the shared verdict shape.
 *
 * HARD CONSTRAINT (same as placement-matrix.ts / placement-resolution.ts):
 * every module in this directory must stay React-free, DOM-free, Node-free
 * and `@/`-alias-free, with relative imports carrying explicit `.ts`
 * extensions, so Deno can resolve the whole tree through the
 * `supabase/functions/_shared/content-quality.ts` re-export stub. The one
 * exception is `src/components/ui/inspector/QualityChip.tsx`, which is a
 * React consumer and never imported by the edge.
 *
 * THE INVARIANT THAT MAKES THIS LANE WORK: `QualityVerdict.checks` always
 * contains EVERY check in `CHECK_ORDER`, and `detail` is a full sentence
 * even when the status is `pass`. Filtering to failures is what the existing
 * readiness sentence in PostBody does, and it is exactly why a user cannot
 * tell "twelve constraints were checked and all passed" from "nothing ran".
 */

/**
 * `skipped` is load-bearing and NOT a synonym for `pass`. It means "this
 * check could not be run against the data available here" — most importantly
 * the aspect check server-side, where image dimensions are genuinely unknown
 * (see grade-artifact.ts). Reporting `pass` on unknown would be a fabricated
 * success.
 */
export type QualityStatus = 'pass' | 'warn' | 'fail' | 'skipped';

export type QualityCheckId =
  | 'format'
  | 'aspect'
  | 'media_kind'
  | 'caption_length'
  | 'caption_fold'
  | 'link'
  | 'caption_cta'
  | 'caption_scene'
  | 'caption_slop'
  | 'image_subject'
  | 'platform_rules';

export type QualityFixId =
  'set_crop_reencode_jpeg' | 'apply_recommended_ratio' | 'trim_caption_to_limit';

/**
 * A one-click remedy DESCRIPTOR. This module applies nothing and touches no
 * network — `perPlatformPatch` is deliberately the same shape
 * `aspectRatioPatch()` produces in
 * `src/components/ui/inspector/bodies/post/mediaKeysWriter.ts`, so the UI
 * applies a fix by handing it to the EXISTING `commitMediaKeysPatches` path
 * rather than introducing a second `media_keys` writer (that module's header
 * documents the 3-writer clobber hazard it exists to prevent).
 */
export interface QualityFix {
  id: QualityFixId;
  label: string;
  platform: string;
  perPlatformPatch?: { set?: { aspectRatio?: string }; unset?: string[] };
  captionReplacement?: string;
}

export interface QualityCheck {
  id: QualityCheckId;
  label: string;
  status: QualityStatus;
  /** A full user-facing sentence, present on EVERY status including `pass`. */
  detail: string;
  fix?: QualityFix | null;
}

export interface QualityVerdict {
  version: 1;
  platform: string;
  placement: string | null;
  mediaKind: 'image' | 'video' | 'carousel' | 'text';
  gradedAt: string;
  status: 'pass' | 'warn' | 'fail';
  /** ALWAYS every id in CHECK_ORDER, in that order, never filtered. */
  checks: QualityCheck[];
  passCount: number;
  warnCount: number;
  failCount: number;
  skippedCount: number;
}

/** What lands in `social_posts.quality` — one verdict per targeted platform. */
export interface QualityBag {
  version: 1;
  gradedAt: string;
  worst: 'pass' | 'warn' | 'fail';
  byPlatform: Record<string, QualityVerdict>;
}

export const CHECK_ORDER: readonly QualityCheckId[] = [
  'format',
  'aspect',
  'media_kind',
  'caption_length',
  'caption_fold',
  'link',
  // The three substance checks (checks/caption-substance.ts). Publisher
  // constraints first, then "is this caption worth publishing at all" —
  // the dimension the 2026-07-31 audit scored 1 out of 5.
  'caption_cta',
  'caption_scene',
  'caption_slop',
  // Check 10 (checks/image-subject.ts) — the only check that compares the two
  // halves of a post to each other. `caption_scene` asks whether the caption
  // talks about a picture; this asks whether the picture is about the caption.
  // Four of four generated posts failed it in the run that motivated it.
  'image_subject',
  // Check 11 (checks/platform-rules.ts) — the only check about the DESTINATION
  // platform's own rules rather than the artifact. Scoped to Product Hunt
  // launches; a generated caption that solicits votes can get the user's
  // listing penalised, which is a correctness failure, not a style one.
  'platform_rules',
] as const;

/**
 * TOTAL record on purpose (same discipline as `RATIO_DESCRIPTION` in
 * AspectRatioPicker.tsx): adding a `QualityCheckId` without a human label is
 * a compile error rather than a blank row in the popover.
 */
export const CHECK_LABEL: Record<QualityCheckId, string> = {
  format: 'File format',
  aspect: 'Image shape',
  media_kind: 'Post type',
  caption_length: 'Caption length',
  caption_fold: 'Opening line',
  link: 'Link',
  caption_cta: 'Call to action',
  caption_scene: 'Image references',
  caption_slop: 'Stock phrasing',
  image_subject: 'Image matches caption',
  platform_rules: 'Platform rules',
};
